import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wordmark } from "../components/Logo";
import { MatrixRain } from "../components/MatrixRain";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { authenticate } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      authenticate(data);
      toast.success(mode === "login" ? t.toastLogin : t.toastRegister);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080A0C] flex items-center justify-center relative terminal-grid px-5">
      <MatrixRain opacity={0.18} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080A0C]/30 to-[#080A0C]" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md glass-card rounded-2xl p-8 sm:p-10">
        <Link to="/" data-testid="auth-home-link" className="flex justify-center mb-8"><Wordmark size={36} /></Link>
        <div className="flex rounded-full border border-[#1F2937] p-1 mb-8 font-mono text-xs">
          {[["login", t.authLogin], ["register", t.authRegister]].map(([m, label]) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2.5 rounded-full tracking-widest transition-all ${mode === m ? "bg-[#00FF66] text-[#080A0C] font-bold" : "text-gray-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name-input" className="gc-input" placeholder={t.phName} value={form.name} onChange={set("name")} required minLength={2} />
          )}
          <input data-testid="auth-email-input" className="gc-input" type="email" placeholder={t.phEmail} value={form.email} onChange={set("email")} required />
          <input data-testid="auth-password-input" className="gc-input" type="password" placeholder={t.phPassword} value={form.password} onChange={set("password")} required minLength={6} />
          {error && <p data-testid="auth-error-msg" className="font-mono text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{error}</p>}
          <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="btn-neon w-full py-3.5 text-sm disabled:opacity-50">
            {busy ? t.processing : mode === "login" ? t.btnLogin : t.btnRegister}
          </button>
        </form>
        <p className="mt-6 text-center font-mono text-[10px] text-gray-600 tracking-wider">{t.authFooter}</p>
      </motion.div>
    </div>
  );
}
